import {
  useMemo,
  useImperativeHandle,
  useRef,
  useCallback,
  useState,
} from "react";

import { type ChatSiderComponentProps } from "./_types";

import { useAuthContext } from "~/common/contexts/auth";
import { usePopupContext } from "~/common/contexts/knowledge";
import { useComponentLogger } from "~/libs/logger";
import { useDeleteSession } from "~/services/chat-session/delete";
import { useRenameConversation } from "~/services/chat-session/patch";

/**
 * ChatSider Component Hooks
 */
export const useChatSider = ({
  ref,
  open,
  onClose,
  onReload,
}: ChatSiderComponentProps) => {
  /** Logger */
  const logger = useComponentLogger("ChatSider");

  /** divRef */
  const divRef = useRef<HTMLDivElement>(null);

  /** auth context */
  const { external_user } = useAuthContext();
  /** Popup context */
  const { states, events } = usePopupContext();
  const { curConversationId, conversationItems } = states;
  const { handleAddConversation, handleActiveChange, resetConversationState } =
    events;

  /** Log component initialization */

  /** useImperativeHandle */
  useImperativeHandle(ref, () => ({ ...divRef.current! }), []);

  /** State */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");

  /** Service */
  const { execute: deleteSession } = useDeleteSession(external_user);
  const { execute: renameConversation } = useRenameConversation(
    external_user,
    editingId || "",
  );

  /** Handle close */
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  /** Handle add conversation - close sidebar after adding  */
  const handleAddConversationWithClose = useCallback(async () => {
    await handleAddConversation();
    handleClose();
  }, [handleAddConversation, handleClose]);

  /** Handle active change - close sidebar after selection  */
  const handleActiveChangeWithClose = useCallback(
    async (val: string) => {
      await handleActiveChange(val);
      handleClose();
    },
    [handleActiveChange, handleClose],
  );

  /**
   * Handle rename conversation - open inline edit
   */
  const handleOpenRenameEdit = useCallback(
    (id: string) => {
      const conversation = conversationItems?.find((item) => item.key === id);
      if (conversation) {
        setEditingId(id);
        setEditingValue(
          typeof conversation.label === "string" ? conversation.label : "",
        );
      }
    },
    [conversationItems],
  );

  /**
   * Handle confirm rename
   */
  const handleConfirmRename = useCallback(async () => {
    if (!editingValue.trim()) {
      setEditingId(null);
      setEditingValue("");
      return;
    }

    try {
      await renameConversation({ name: editingValue });
      setEditingId(null);
      setEditingValue("");
      onReload?.();
    } catch (error) {
      logger.error("Rename conversation failed", error);
    }
  }, [editingValue, renameConversation, logger, onReload]);

  /**
   * Handle cancel rename
   */
  const handleCancelRename = useCallback(() => {
    setEditingId(null);
    setEditingValue("");
  }, []);

  /** Handle delete conversation */
  const handleDeleteConversation = useCallback(
    async (id?: string) => {
      if (!id) return;
      try {
        await deleteSession({
          session_ids: [id],
        });
        if (curConversationId === id) {
          resetConversationState();
        }
        onReload?.();
      } catch (error) {
        logger.error("Delete conversation failed", error);
      }
    },
    [
      deleteSession,
      logger,
      onReload,
      curConversationId,
      resetConversationState,
    ],
  );

  /** return */
  return useMemo(
    () => ({
      divRef,
      open: open ?? false,
      conversationItems,
      curConversationId,
      handleClose,
      handleAddConversationWithClose,
      handleActiveChangeWithClose,
      handleDeleteConversation,
      editingId,
      editingValue,
      handleOpenRenameEdit,
      handleConfirmRename,
      handleCancelRename,
      setEditingValue,
    }),
    [
      open,
      conversationItems,
      curConversationId,
      handleClose,
      handleAddConversationWithClose,
      handleActiveChangeWithClose,
      handleDeleteConversation,
      editingId,
      editingValue,
      handleOpenRenameEdit,
      handleConfirmRename,
      handleCancelRename,
    ],
  );
};
